import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, RadioGroup, FormControlLabel,
  Radio, FormControl, InputLabel, Select, MenuItem, Checkbox,
  Grid, Divider, Alert, CircularProgress, Chip, Paper
} from '@mui/material';
import apiClient from '@/api/client';

// Helper to highlight differences
const getDiffStyle = (incomingValue, storedValue) => {
  if (incomingValue && storedValue && incomingValue !== storedValue) {
    return {
      backgroundColor: '#ffebee',
      border: '1px solid #ef5350',
      borderRadius: '4px',
      padding: '2px 6px',
      display: 'inline-block',
    };
  }
  return {};
};

const MemberConflictResolutionDialog = ({ open, conflict, onClose }) => {
  const [action, setAction] = useState('update');
  const [memberId, setMemberId] = useState(null);
  const [fieldDecisions, setFieldDecisions] = useState({});
  const [fieldActions, setFieldActions] = useState({});
  const [moveOldToSecondary, setMoveOldToSecondary] = useState(false);
  const [contactsToAdd, setContactsToAdd] = useState([]);
  const [targetMemberId, setTargetMemberId] = useState(null);
  const [sourceMemberId, setSourceMemberId] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open && conflict) {
      setAction('update');
      setFieldDecisions({});
      setFieldActions({});
      setMoveOldToSecondary(false);
      setContactsToAdd([]);
      setNotes('');
      setError('');
      setSubmitting(false);

      const primaryMemberId = conflict.matched_member_id || conflict.matched_member_id_2;
      setMemberId(primaryMemberId);

      if (conflict.matched_member_id && conflict.matched_member_id_2) {
        setTargetMemberId(conflict.matched_member_id);
        setSourceMemberId(conflict.matched_member_id_2);
      } else {
        setTargetMemberId(null);
        setSourceMemberId(null);
      }

      // Default actions: replace for fields that differ, ignore otherwise
      const fields = ['first_name', 'last_name', 'email', 'mobile_number', 'wt_number'];
      const initialActions = {};
      fields.forEach(field => {
        const incomingField = field === 'mobile_number' ? 'incoming_mobile' : (field === 'wt_number' ? 'incoming_whatsapp' : `incoming_${field}`);
        const incomingValue = conflict[incomingField] || '';
        const currentValue = conflict.matched_member?.[field] || '';
        if (incomingValue && currentValue && incomingValue !== currentValue) {
          initialActions[field] = 'replace';
          // Pre‑fill decision with incoming value
          setFieldDecisions(prev => ({ ...prev, [field]: incomingValue }));
        } else {
          initialActions[field] = 'ignore';
        }
      });
      setFieldActions(initialActions);
    }
  }, [open, conflict]);

  const handleFieldActionChange = (field, action, customValue = null) => {
    setFieldActions(prev => ({ ...prev, [field]: action }));
    if (action === 'replace' || action === 'custom') {
      const incomingField = field === 'mobile_number' ? 'incoming_mobile' : (field === 'wt_number' ? 'incoming_whatsapp' : `incoming_${field}`);
      const incomingValue = conflict[incomingField] || '';
      setFieldDecisions(prev => ({ ...prev, [field]: action === 'replace' ? incomingValue : (customValue || '') }));
    } else if (action === 'add_secondary') {
      // Add to contacts
      const incomingField = field === 'mobile_number' ? 'incoming_mobile' : (field === 'wt_number' ? 'incoming_whatsapp' : `incoming_${field}`);
      const incomingValue = conflict[incomingField] || '';
      if (incomingValue) {
        setContactsToAdd(prev => {
          // Avoid duplicates
          if (!prev.some(c => c.type === field && c.value === incomingValue)) {
            return [...prev, { type: field === 'mobile_number' ? 'mobile' : (field === 'wt_number' ? 'whatsapp' : field), value: incomingValue }];
          }
          return prev;
        });
      }
    } else if (action === 'ignore') {
      // Remove from decisions and contacts
      setFieldDecisions(prev => {
        const newDecisions = { ...prev };
        delete newDecisions[field];
        return newDecisions;
      });
      setContactsToAdd(prev => prev.filter(c => c.type !== field));
    }
  };

  const handleMergeDecisionChange = (field, decision, customValue = null) => {
    setFieldDecisions(prev => ({ ...prev, [`${field}_decision`]: decision, [`${field}_custom`]: customValue }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    const payload = {
      action,
      coordinator_notes: notes,
    };

    switch (action) {
      case 'update':
        payload.member_id = memberId;
        // Only include fields where action is replace or custom
        const finalFieldDecisions = {};
        Object.keys(fieldActions).forEach(field => {
          const act = fieldActions[field];
          if (act === 'replace' && fieldDecisions[field]) {
            finalFieldDecisions[field] = fieldDecisions[field];
          } else if (act === 'custom' && fieldDecisions[field]) {
            finalFieldDecisions[field] = fieldDecisions[field];
          }
        });
        payload.field_decisions = finalFieldDecisions;
        payload.move_old_to_secondary = moveOldToSecondary;
        // Include contacts (from add_secondary actions)
        if (contactsToAdd.length > 0) {
          payload.contacts = contactsToAdd;
        }
        break;
      case 'merge':
        payload.target_member_id = targetMemberId;
        payload.source_member_id = sourceMemberId;
        const mergeDecisions = {};
        ['first_name', 'last_name', 'email', 'mobile_number', 'wt_number'].forEach(field => {
          const decision = fieldDecisions[`${field}_decision`];
          if (decision === 'replace') {
            mergeDecisions[field] = fieldDecisions[`${field}_replace_value`] || conflict[`incoming_${field === 'mobile_number' ? 'mobile' : (field === 'wt_number' ? 'whatsapp' : field)}`];
          } else if (decision === 'add_secondary') {
            mergeDecisions[`secondary_${field}`] = 'add';
          } else if (decision === 'custom') {
            mergeDecisions[field] = fieldDecisions[`${field}_custom`];
          }
        });
        payload.field_decisions = mergeDecisions;
        break;
      case 'reject':
        break;
      default:
        break;
    }

    try {
      await apiClient.post(`/admin/conflicts/${conflict.id}/resolve`, payload);
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Resolution failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!conflict) return null;

  const isCrossMember = !!(conflict.matched_member_id && conflict.matched_member_id_2);
  const primaryMember = conflict.matched_member || conflict.matched_member_2;

  const renderUpdateForm = () => {
    const fields = ['first_name', 'last_name', 'email', 'mobile_number', 'wt_number'];
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Field‑wise Actions</Typography>
        <Grid container spacing={2}>
          {fields.map(field => {
            const incomingField = field === 'mobile_number' ? 'incoming_mobile' : (field === 'wt_number' ? 'incoming_whatsapp' : `incoming_${field}`);
            const incomingValue = conflict[incomingField] || '';
            const currentValue = primaryMember?.[field] || '';
            const currentAction = fieldActions[field] || 'ignore';
            const isDiff = incomingValue !== currentValue && incomingValue && currentValue;

            return (
              <Grid item xs={12} key={field}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: isDiff ? '#fff8e1' : 'inherit' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3}>
                      <Typography variant="subtitle2">{field.replace(/_/g, ' ').toUpperCase()}</Typography>
                      <Typography variant="caption" display="block">
                        Current: <strong>{currentValue || '—'}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        Incoming: <strong style={{ color: isDiff ? '#d32f2f' : 'inherit' }}>{incomingValue || '—'}</strong>
                      </Typography>
                      {isDiff && <Chip label="Differs" size="small" color="warning" sx={{ mt: 0.5 }} />}
                    </Grid>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Action</InputLabel>
                        <Select
                          value={currentAction}
                          label="Action"
                          onChange={(e) => handleFieldActionChange(field, e.target.value)}
                        >
                          <MenuItem value="ignore">Ignore</MenuItem>
                          <MenuItem value="replace">Replace primary</MenuItem>
                          <MenuItem value="add_secondary">Add as secondary</MenuItem>
                          <MenuItem value="custom">Custom value</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      {(currentAction === 'replace' || currentAction === 'custom') && (
                        <TextField
                          fullWidth
                          size="small"
                          label={currentAction === 'replace' ? 'Replace with value' : 'Custom value'}
                          value={fieldDecisions[field] || (currentAction === 'replace' ? incomingValue : '')}
                          onChange={(e) => {
                            setFieldDecisions(prev => ({ ...prev, [field]: e.target.value }));
                            if (currentAction === 'custom') {
                              setFieldActions(prev => ({ ...prev, [field]: 'custom' }));
                            }
                          }}
                        />
                      )}
                      {currentAction === 'add_secondary' && incomingValue && (
                        <Typography variant="caption" color="success.main">
                          ✅ Will be added as secondary contact
                        </Typography>
                      )}
                      {currentAction === 'ignore' && (
                        <Typography variant="caption" color="text.secondary">
                          No changes
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <FormControlLabel
          control={
            <Checkbox
              checked={moveOldToSecondary}
              onChange={(e) => setMoveOldToSecondary(e.target.checked)}
            />
          }
          label="Move old values to secondary contacts (for fields being replaced)"
          sx={{ mt: 2 }}
        />
      </Box>
    );
  };

  const renderMergeForm = () => {
    if (!isCrossMember) return <Alert severity="warning">This conflict does not involve two members.</Alert>;
    const memberA = conflict.matched_member;
    const memberB = conflict.matched_member_2;
    const target = targetMemberId === memberA?.id ? memberA : memberB;
    const source = targetMemberId === memberA?.id ? memberB : memberA;
    if (!target || !source) return null;

    const fields = ['first_name', 'last_name', 'email', 'mobile_number', 'wt_number'];
    return (
      <Box>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Target Member</InputLabel>
          <Select
            value={targetMemberId || ''}
            onChange={(e) => {
              const newTarget = parseInt(e.target.value);
              setTargetMemberId(newTarget);
              setSourceMemberId(newTarget === memberA.id ? memberB.id : memberA.id);
              const newDecisions = {};
              fields.forEach(field => {
                newDecisions[`${field}_decision`] = 'keep';
              });
              setFieldDecisions(newDecisions);
            }}
          >
            <MenuItem value={memberA?.id}>Member A: {memberA?.first_name} {memberA?.last_name} (ID {memberA?.id})</MenuItem>
            <MenuItem value={memberB?.id}>Member B: {memberB?.first_name} {memberB?.last_name} (ID {memberB?.id})</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="subtitle1" gutterBottom>Field‑by‑field decisions</Typography>
        {fields.map(field => {
          const targetValue = target?.[field] || '';
          const sourceValue = source?.[field] || '';
          const incomingValue = conflict[`incoming_${field === 'mobile_number' ? 'mobile' : (field === 'wt_number' ? 'whatsapp' : field)}`] || '';
          const decisionKey = `${field}_decision`;
          const decision = fieldDecisions[decisionKey] || 'keep';
          return (
            <Box key={field} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle2">{field.replace(/_/g, ' ').toUpperCase()}</Typography>
              <Typography variant="caption" display="block" gutterBottom>
                Target: {targetValue} | Source: {sourceValue} | Incoming: {incomingValue}
              </Typography>
              <RadioGroup
                row
                value={decision}
                onChange={(e) => handleMergeDecisionChange(field, e.target.value)}
              >
                <FormControlLabel value="keep" control={<Radio />} label="Keep target" />
                <FormControlLabel value="replace" control={<Radio />} label="Replace with source" />
                <FormControlLabel value="add_secondary" control={<Radio />} label="Add source as secondary" />
                <FormControlLabel value="custom" control={<Radio />} label="Custom value" />
              </RadioGroup>
              {decision === 'replace' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Replace with value"
                  value={fieldDecisions[`${field}_replace_value`] || sourceValue}
                  onChange={(e) => setFieldDecisions(prev => ({ ...prev, [`${field}_replace_value`]: e.target.value }))}
                  sx={{ mt: 1 }}
                />
              )}
              {decision === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom value"
                  value={fieldDecisions[`${field}_custom`] || ''}
                  onChange={(e) => setFieldDecisions(prev => ({ ...prev, [`${field}_custom`]: e.target.value }))}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Resolve Conflict #{conflict.id}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="subtitle1" fontWeight="bold">Incoming Data</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Name:</strong> {conflict.incoming_first_name} {conflict.incoming_last_name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {conflict.incoming_email}</Typography>
              <Typography variant="body2"><strong>Mobile:</strong> {conflict.incoming_mobile}</Typography>
              <Typography variant="body2"><strong>WhatsApp:</strong> {conflict.incoming_whatsapp || '—'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle1" fontWeight="bold">Existing Member(s)</Typography>
            {primaryMember && (
              <Box>
                <Chip label="Primary" size="small" color="primary" sx={{ mb: 1 }} />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2"><strong>Name:</strong> {primaryMember.first_name} {primaryMember.last_name}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {primaryMember.email}</Typography>
                  <Typography variant="body2"><strong>Mobile:</strong> {primaryMember.mobile_number}</Typography>
                  <Typography variant="body2"><strong>WhatsApp:</strong> {primaryMember.wt_number || '—'}</Typography>
                </Box>
              </Box>
            )}
            {isCrossMember && conflict.matched_member_2 && (
              <Box sx={{ mt: 2 }}>
                <Chip label="Second Match" size="small" color="secondary" sx={{ mb: 1 }} />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2"><strong>Name:</strong> {conflict.matched_member_2.first_name} {conflict.matched_member_2.last_name}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {conflict.matched_member_2.email}</Typography>
                  <Typography variant="body2"><strong>Mobile:</strong> {conflict.matched_member_2.mobile_number}</Typography>
                  <Typography variant="body2"><strong>WhatsApp:</strong> {conflict.matched_member_2.wt_number || '—'}</Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>Resolution Action</Typography>
        <RadioGroup row value={action} onChange={(e) => setAction(e.target.value)}>
          <FormControlLabel value="update" control={<Radio />} label="Custom Resolution" />
          {isCrossMember && <FormControlLabel value="merge" control={<Radio />} label="Merge Members" />}
          <FormControlLabel value="reject" control={<Radio />} label="Reject" />
        </RadioGroup>

        <Box sx={{ mt: 3 }}>
          {action === 'update' && renderUpdateForm()}
          {action === 'merge' && renderMergeForm()}
          {action === 'reject' && <Alert severity="info">The conflict will be rejected and no changes will be made.</Alert>}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Coordinator Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 3 }}
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={submitting}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Resolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberConflictResolutionDialog;